import { GameServices } from "game/GameServices";
import { OpenGameEvent } from "servertypes/gameevents/OpenGame";
import { LuckyWealthCatGameModel } from "game/LuckyWealthCatGameModel";
import { ReelStopEase, SpinConfig, SpinType } from "components/reels/ReelsModel";
import { copyToClipboard, loadFile, saveFile } from "utils/Misc";
import { JackpotBowl } from "components/jackpotbowl/JackpotBowl";
import { BonusTransitionPanels } from "components/BonusTransitionPanels";
import { BonusSummaryScroll } from "components/BonusSummaryScroll";
import { MultiplierPanels } from "components/multiplierpanels/MultiplierPanels";
import { CollectionSymbol } from "components/CollectionSymbol";
import { Tween } from "@westpierstudio/luma/createjs";
import { CoreSymbolID, getMultiplierPanelIndex, JackpotWedgeID } from "servertypes/SharedTypes";
import { WheelPrizePanel } from "components/wheelprizepanel/WheelPrizePanel";
import { LuckyWealthCatGameView } from "game/LuckyWealthCatGameView";
import { GameDevGUI } from "@westpierstudio/core-module-oryx/utils/devgui";
import { GUI } from "dat.gui";
import { StandardMaterial } from "pixi3d";

type CoinBowlSettings = {
    materials: {
        bowl: {
            tint: number[],
            roughness: number,
            metallic: number,
            exposure: number
        },
        coin: {
            tint: number[],
            roughness: number,
            metallic: number,
            exposure: number
        }
    },
    physics: {
        gravity: number,
        restitution: number,
        friction: number,
        linearDamping: number,
        angularDamping: number,
    }
}

export class LuckyWealthCatDebugGUI extends GameDevGUI {

    constructor(
        protected override readonly services: GameServices,
        protected readonly model: LuckyWealthCatGameModel,
    ) {
        super(services);
    }

    setupSpinConfigEditor(): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableSpinConfigEditor) {

            const spinSection = this.gui.addSubSection(`Spin Config`);
            const spinTypes: SpinType[] = Object.keys(this.services.resources.getSetting(`reelSpinSettings`)) as SpinType[];

            for (const type of spinTypes) {

                const spinTypeSection = spinSection.addFolder(type);

                const spinConfig = this.model.reels.spinConfigs.get(type) as SpinConfig;
                const spinSettings = this.model.reels.getSettingsForConfig(spinConfig);

                for (const [key, item] of Object.entries(spinSettings)) {

                    const obj = {value: item};

                    if (typeof item === `number`) {

                        this.gui.addNumber(key, obj, () => {

                            (spinConfig as any)[key] = obj.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);

                    } else if (typeof item === `boolean`) {

                        this.gui.addCheckBox(key, obj, () => {

                            (spinConfig as any)[key] = obj.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);
                    } else {

                        const list = Object.values(ReelStopEase);
                        const state = {value: list[list.indexOf(item)]};

                        this.gui.addDropdown(key, list, state, () => {

                            (spinConfig as any)[key] = state.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);
                    }
                }

                this.gui.addButton(`Copy`, () => {

                    const spinSettings = this.model.reels.getSettingsForConfig(spinConfig);
                    copyToClipboard(JSON.stringify(spinSettings, null, `\t`));
                }, spinTypeSection);
            }

            this.gui.addButton(`Reset Defaults`, () => {
                this.model.reels.restoreDefaultSpinConfigs();
                this.gui.restoreSectionDefaults(spinSection);
            }, spinSection);
        }
    }

    setupMultiplierPanelDebug(multiplierPanels: MultiplierPanels, collectionSymbol: CollectionSymbol): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableMultiplierPanelDebug) {

            const multiSection = this.gui.addSubSection(`Multiplier Panel`);
            multiSection.open();

            let symbolIndex = 0;
            let panelIndex = 0;

            const symbolIndexObj = {value: symbolIndex};
            const targetPanelObj = {value: panelIndex};

            this.gui.addNumber(`SymbolIndex`, symbolIndexObj, () => {
                symbolIndex = symbolIndexObj.value;
            }, multiSection);

            this.gui.addNumber(`TargetPanel`, targetPanelObj, () => {
                panelIndex = targetPanelObj.value;
            }, multiSection);

            this.gui.addButton(`CollectSymbol`, () => {

                const firstReelSymbolPositions = this.model.reels.layout.globalSymbolPositions[0];

                multiplierPanels.resetPanel(panelIndex);
                collectionSymbol.configureForCollection(`M1`, firstReelSymbolPositions[symbolIndex], 1.0, false);

                Tween.get(this)
                .wait(1)
                .call(() => {
                    collectionSymbol.collect(`M1`, panelIndex, this.model.multiplierPanels.layout);
                });

                Tween.get(this)
                .wait(resources.getSetting(`multiplierPanel`).showDelay)
                .call(() => {
                    multiplierPanels.showPanel(panelIndex, `M1`, 5, 7);
                });

            }, multiSection);

            this.gui.addButton(`ChangeMultiplier`, () => {

                let symbolID: CoreSymbolID;
                let panelIndex: number;

                const symbolIDs = Object.keys(this.model.multiplierCountersState[2]);
                const updateOrder = [`M1`, `M2`, `M3`, `F4`, `F5`, `F6`];
                const transitionTime = this.services.resources.getSetting(`multiplierPanel`).transitionTimePerPanel;

                const tween = Tween.get(this);

                for (const id of updateOrder) {

                    if (symbolIDs.includes(id)) {

                        tween.call(() => {

                            symbolID = id as CoreSymbolID;
                            panelIndex = getMultiplierPanelIndex(symbolID);

                            multiplierPanels.setPanel(
                                panelIndex,
                                symbolID,
                                10,
                                5 as number,
                                true
                                );
                        })
                        .wait(transitionTime);
                    }
                }

            }, multiSection);
        }
    }

    setupJackpotCoinBowlDebug(jackpotBowl: JackpotBowl): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableJackpotCoinBowlDebug) {

            const coinBowlTab = this.gui.addSubSection(`Coin Bowl`);
            coinBowlTab.close();

            const renderingTab = coinBowlTab.addFolder(`Rendering`);
            const bowlMaterialTab = renderingTab.addFolder(`Bowl Material`);
            const coinMaterialTab = renderingTab.addFolder(`Coin Material`);

            const materials = jackpotBowl.getDebugMaterials();

            const bowlMaterialControl = this.setupMaterial(bowlMaterialTab, materials.bowl);
            const coinMaterialControl = this.setupMaterial(coinMaterialTab, materials.coin, (r, g, b, a) => jackpotBowl.setCoinTint(r,g,b,a));

            const physicsTab = coinBowlTab.addFolder(`Physics`);

            const {world, coinProperties} = jackpotBowl.getDebugPhsyics();
            const gravity = {value: Math.abs(world.getGravity().z())};
            const restitution = {value: coinProperties.restitution};
            const friction = {value: coinProperties.friction};
            const linearDamping = {value: coinProperties.damping.linear};
            const angularDamping = {value: coinProperties.damping.angular};
            const gravityControl = this.gui.addNumberSlider(`gravity`, gravity, 0, 3, 0.001, () => jackpotBowl.setDebugGravity(-gravity.value), true, physicsTab);
            const restitutionControl = this.gui.addNumberSlider(`restitution`, restitution, 0, 5, 0.001, () => {
                coinProperties.restitution = restitution.value;
                jackpotBowl.setDebugCoinPhysicsProperties(coinProperties);
            }, true, physicsTab);
            const frictionControl = this.gui.addNumberSlider(`friction`, friction, 0, 5, 0.001, () => {
                coinProperties.friction = friction.value;
                jackpotBowl.setDebugCoinPhysicsProperties(coinProperties);
            }, true, physicsTab);
            const linearDampingControl = this.gui.addNumberSlider(`linear damping`, linearDamping, 0, 5, 0.001, () => {
                coinProperties.damping.linear = linearDamping.value;
                jackpotBowl.setDebugCoinPhysicsProperties(coinProperties);
            }, true, physicsTab);
            const angularDampingControl = this.gui.addNumberSlider(`angular damping`, angularDamping, 0, 5, 0.001, () => {
                coinProperties.damping.angular = angularDamping.value;
                jackpotBowl.setDebugCoinPhysicsProperties(coinProperties);
            }, true, physicsTab);

            const coinCount = {value: jackpotBowl.getDebugCoinCount()};
            const countController = this.gui.addNumber(`Coin Count`, coinCount, undefined, coinBowlTab);
            jackpotBowl.setDebugCoinCountUpdater((count) => {
                coinCount.value = count;
                countController.updateDisplay();
            });

            const coinReactionEnabled = {value: jackpotBowl.getDebugCoinReactionEnabled()};
            this.gui.addCheckBox(`Enable coin reaction animation`, coinReactionEnabled, () => {
                jackpotBowl.setDebugCoinReactionEnabled(coinReactionEnabled.value);
            }, coinBowlTab);
            this.gui.addButton(`Spawn Coin`, () => {
                jackpotBowl.spawnNewCoin({x: window.innerWidth / 2, y: window.innerHeight / 2});
            }, coinBowlTab);
            this.gui.addButton(`Empty Bowl`, () => {
                jackpotBowl.jackpotSequenceRemoveCoins(0);
            }, coinBowlTab);
            this.gui.addButton(`Play Jackpot Win Sequence (shortened)`, () => {
                jackpotBowl.startWinLoop();
                Tween.get(this)
                    .wait(5000)
                    .call(() => jackpotBowl.endWinLoop());
            }, coinBowlTab);

            this.gui.addButton(`Console Log Settings`, () => {
                console.log(`######## COIN BOWL SETTINGS ########\n`);
                console.log(`\n[BOWL MATERIAL]`);
                console.log(`tint rgba:`, JSON.stringify(Array.from(materials.bowl.baseColor.rgba)));
                console.log(`roughness:`, Number(materials.bowl.roughness.toFixed(3)));
                console.log(`metallic:`, Number(materials.bowl.metallic.toFixed(3)));
                console.log(`exposure:`, Number(materials.bowl.exposure.toFixed(3)));
                console.log(`\n[COIN MATERIAL]`);
                console.log(`tint rgba:`, JSON.stringify(Array.from(materials.coin.baseColor.rgba)));
                console.log(`roughness:`, Number(materials.coin.roughness.toFixed(3)));
                console.log(`metallic:`, Number(materials.coin.metallic.toFixed(3)));
                console.log(`exposure:`, Number(materials.coin.exposure.toFixed(3)));
                console.log(`\n[PHYSICS]`);
                console.log(`gravity:`, -Number(gravity.value.toFixed(3)));
                console.log(`restitution:`, Number(restitution.value.toFixed(3)));
                console.log(`friction:`, Number(friction.value.toFixed(3)));
                console.log(`linearDamping:`, Number(linearDamping.value.toFixed(3)));
                console.log(`angularDamping:`, Number(angularDamping.value.toFixed(3)));
            }, coinBowlTab);

            this.gui.addButton(`Save To File`, () => {

                const settings: CoinBowlSettings = {
                    materials: {
                        bowl: {
                            tint: Array.from(materials.bowl.baseColor.rgba),
                            roughness: Number(materials.bowl.roughness.toFixed(3)),
                            metallic: Number(materials.bowl.metallic.toFixed(3)),
                            exposure: Number(materials.bowl.exposure.toFixed(3))
                        },
                        coin: {
                            tint: Array.from(materials.coin.baseColor.rgba),
                            roughness: Number(materials.coin.roughness.toFixed(3)),
                            metallic: Number(materials.coin.metallic.toFixed(3)),
                            exposure: Number(materials.coin.exposure.toFixed(3))
                        }
                    },
                    physics: {
                        gravity: -Number(gravity.value.toFixed(3)),
                        restitution: Number(restitution.value.toFixed(3)),
                        friction: Number(friction.value.toFixed(3)),
                        linearDamping: Number(linearDamping.value.toFixed(3)),
                        angularDamping: Number(angularDamping.value.toFixed(3)),
                    }
                };

                saveFile(JSON.stringify(settings, null, `\t`), `settings.json`);
            }, coinBowlTab);

            this.gui.addButton(`Load from File`, async () => {
                const data = await loadFile();
                if (data) {
                    const settings = data as CoinBowlSettings;

                    bowlMaterialControl.tintRControl.setValue(settings.materials.bowl.tint[0]);
                    bowlMaterialControl.tintGControl.setValue(settings.materials.bowl.tint[1]);
                    bowlMaterialControl.tintBControl.setValue(settings.materials.bowl.tint[2]);
                    bowlMaterialControl.tintAControl.setValue(settings.materials.bowl.tint[3]);
                    bowlMaterialControl.metallicControl.setValue(settings.materials.bowl.metallic);
                    bowlMaterialControl.roughnessControl.setValue(settings.materials.bowl.roughness);
                    bowlMaterialControl.exposureControl.setValue(settings.materials.bowl.exposure);

                    coinMaterialControl.tintRControl.setValue(settings.materials.coin.tint[0]);
                    coinMaterialControl.tintGControl.setValue(settings.materials.coin.tint[1]);
                    coinMaterialControl.tintBControl.setValue(settings.materials.coin.tint[2]);
                    coinMaterialControl.tintAControl.setValue(settings.materials.coin.tint[3]);
                    coinMaterialControl.metallicControl.setValue(settings.materials.coin.metallic);
                    coinMaterialControl.roughnessControl.setValue(settings.materials.coin.roughness);
                    coinMaterialControl.exposureControl.setValue(settings.materials.coin.exposure);

                    gravityControl.setValue(Math.abs(settings.physics.gravity));
                    restitutionControl.setValue(settings.physics.restitution);
                    frictionControl.setValue(settings.physics.friction);
                    linearDampingControl.setValue(settings.physics.linearDamping);
                    angularDampingControl.setValue(settings.physics.angularDamping);
                }
            }, coinBowlTab);

        }
    }

    setupMaterial(tab: GUI, material: StandardMaterial, setTint?: (r: number, g: number, b: number, a: number) => void) {
        const roughness = {value: material.roughness};
        const metallic = {value: material.metallic};
        const exposure = {value: material.exposure};

        const tintR = {value: material.baseColor.r};
        const tintG = {value: material.baseColor.g};
        const tintB = {value: material.baseColor.b};
        const tintA = {value: material.baseColor.a};

        const tintRControl = this.gui.addNumberSlider(`tint R`, tintR, 0, 1, 0.01, () => {
            material.baseColor.r = tintR.value;
            if (setTint) {
                setTint(tintR.value, tintG.value, tintB.value, tintA.value);
            }
        }, true, tab);
        const tintGControl = this.gui.addNumberSlider(`tint G`, tintG, 0, 1, 0.01, () => {
            material.baseColor.g = tintG.value;
            if (setTint) {
                setTint(tintR.value, tintG.value, tintB.value, tintA.value);
            }
        }, true, tab);
        const tintBControl = this.gui.addNumberSlider(`tint B`, tintB, 0, 1, 0.01, () => {
            material.baseColor.b = tintB.value;
            if (setTint) {
                setTint(tintR.value, tintG.value, tintB.value, tintA.value);
            }
        }, true, tab);
        const tintAControl = this.gui.addNumberSlider(`tint A`, tintA, 0, 1, 0.01, () => {
            material.baseColor.a = tintA.value;
            if (setTint) {
                setTint(tintR.value, tintG.value, tintB.value, tintA.value);
            }
        }, true, tab);

        const roughnessControl = this.gui.addNumberSlider(`roughness`, roughness, 0, 1, 0.01, () => {
            material.roughness = roughness.value;
        }, true, tab);
        const metallicControl = this.gui.addNumberSlider(`metallic`, metallic, 0, 1, 0.01, () => {
            material.metallic = metallic.value;
        }, true, tab);
        const exposureControl = this.gui.addNumberSlider(`exposure`, exposure, 0, 5, 0.01, () => {
            material.exposure = exposure.value;
        }, true, tab);

        return {
            tintRControl,
            tintGControl,
            tintBControl,
            tintAControl,
            roughnessControl,
            metallicControl,
            exposureControl
        };
    }

    setupWheelPrizePanelDebug(view: LuckyWealthCatGameView, wheelPrizePanel: WheelPrizePanel, jackpotBowl: JackpotBowl,
        jackpotBowlLayerIndex: number): void {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableWheelPrizePanelDebug) {

            const wheelPrizeSection = this.gui.addSubSection(`Wheel Prize Panel`);
            wheelPrizeSection.open();

            this.gui.addButton(`ShowCashPanel`, () => {

                wheelPrizePanel.reset();
                wheelPrizePanel.visible = true;
                wheelPrizePanel.showCashPanel(8000);

            }, wheelPrizeSection);

            this.gui.addButton(`ShowFreeSpinsPanel`, () => {

                wheelPrizePanel.reset();
                wheelPrizePanel.visible = true;
                wheelPrizePanel.showFreeSpinsPanel(15, 900);
            }, wheelPrizeSection);

            this.gui.addButton(`ShowExtendFreeSpinsPanel`, () => {

                wheelPrizePanel.reset();
                wheelPrizePanel.visible = true;
                wheelPrizePanel.showExtendFreeSpinsPanel(15, 900);
            }, wheelPrizeSection);

            this.gui.addButton(`ShowJackpotPanel`, () => {

                wheelPrizePanel.reset();
                wheelPrizePanel.visible = true;
                wheelPrizePanel.showJackpotPanel(8000, JackpotWedgeID.JACKPOT_0);

                wheelPrizePanel.jackpotBowlContainer.addChild(jackpotBowl);
                jackpotBowl.startWinLoop();

                jackpotBowl.signals.countUpComplete.add(() => {

                    jackpotBowl.endWinLoop();

                    jackpotBowl.signals.winComplete.add(() => {

                        view.rootGameContent.addChildAt(jackpotBowl, jackpotBowlLayerIndex);
                        wheelPrizePanel.hidePanel();
                    });
                });


            }, wheelPrizeSection);

            this.gui.addButton(`Show2SymbolPanel`, () => {

                wheelPrizePanel.reset();
                wheelPrizePanel.visible = true;
                wheelPrizePanel.showSymbolPanel([`M1`, `F4`], 900);
            }, wheelPrizeSection);

            this.gui.addButton(`Show6SymbolPanel`, () => {

                wheelPrizePanel.reset();
                wheelPrizePanel.visible = true;
                wheelPrizePanel.showSymbolPanel([`M1`, `M2`, `M3`, `F4`, `F5`, `F6`], 900);
            }, wheelPrizeSection);
        }
    }

    setupBonusTransitionDebug(bonusTransitionPanels: BonusTransitionPanels) {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableBonusTransitionDebug) {

            const bonusTransitionTab = this.gui.addSubSection(`Bonus Transition`);
            bonusTransitionTab.open();
            this.gui.addButton(`Play`, () => {
                bonusTransitionPanels.playTransition();
            }, bonusTransitionTab);
        }
    }

    setupBonusSummaryScrollDebug(bonusSummaryScroll: BonusSummaryScroll) {

        const {resources} = this.services;

        if (resources.getSetting(`dev`).enableBonusScrollDebug) {

            const summaryScrollTab = this.gui.addSubSection(`Summary Scroll`);
            summaryScrollTab.open();
            this.gui.addButton(`Show`, () => {

                bonusSummaryScroll.reset();
                bonusSummaryScroll.show(1000);
            }, summaryScrollTab);
        }
    }
}